'use client';

// useUrlExtraction - TanStack Query mutation for URL content extraction
// Story 6.5: Added referrerBotId for viral tracking attribution

import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveSessionToStorage, getOrCreateClientSession } from '../lib/session';
import { getReferral, clearReferral } from '../lib/referralTracking';
import type { ExtractedUrl, UrlStatus } from '../features/content-input/UrlPreviewCard';

/**
 * Request body for URL extraction API
 */
interface ExtractUrlRequest {
  url: string;
  botId?: string;
  crawlLinks?: boolean;
  maxLinks?: number;
  /** Optional referrer bot ID for viral tracking attribution (Story 6.5) */
  referrerBotId?: string;
}

/**
 * Primary page result from API
 */
interface PrimaryResult {
  contentId: string;
  url: string;
  title: string;
  excerpt: string;
  characterCount: number;
  extractionMethod: 'static' | 'browser';
  isPartial: boolean;
  byline?: string;
}

/**
 * Linked page result from API
 */
interface LinkedPageResult {
  contentId: string;
  url: string;
  title: string;
  excerpt: string;
  characterCount: number;
  linkScore: number;
  linkText: string;
  failed?: boolean;
  error?: string;
}

/**
 * Summary statistics from API
 */
interface ExtractionSummary {
  totalPages: number;
  totalCharacters: number;
  crawlDuration: number;
  linksDiscovered: number;
  linksCrawled: number;
  linksSucceeded: number;
}

/**
 * Success response from URL extraction API
 */
interface ExtractUrlResponse {
  data: {
    botId: string;
    primary: PrimaryResult;
    linkedPages: LinkedPageResult[];
    summary: ExtractionSummary;
  };
}

/**
 * Error response from URL extraction API
 */
interface ExtractUrlError {
  error: {
    code: string;
    message: string;
    suggestion?: string;
  };
}

/**
 * Maps API error codes to i18n error keys
 */
function mapErrorCodeToI18nKey(code: string): string {
  switch (code) {
    case 'INVALID_URL':
      return 'invalidUrl';
    case 'URL_BLOCKED':
      return 'urlBlocked';
    case 'FETCH_FAILED':
      return 'fetchFailed';
    case 'TIMEOUT':
      return 'timeout';
    case 'EXTRACTION_FAILED':
      return 'extractionFailed';
    case 'BROWSER_UNAVAILABLE':
      return 'browserUnavailable';
    default:
      return 'extractionFailed';
  }
}

/**
 * Ensures session is synced before extraction
 */
async function ensureSessionSync(): Promise<void> {
  try {
    const localSession = getOrCreateClientSession();

    const response = await fetch('/api/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: localSession }),
    });

    const data = await response.json();
    if (data.data?.sessionId) {
      saveSessionToStorage(data.data.sessionId);
    }
  } catch {
    console.warn('[session] Pre-extraction session sync failed');
  }
}

/**
 * Extracts content from a URL via the API
 */
async function extractUrl(request: ExtractUrlRequest): Promise<ExtractUrlResponse> {
  await ensureSessionSync();

  // Story 6.5: Add referral attribution for new bot creation
  const isNewBot = !request.botId;
  const storedReferral = getReferral();
  const referrerBotId = request.referrerBotId || storedReferral || undefined;

  const response = await fetch('/api/bots/extract-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...request,
      // Only include referrerBotId for new bot creation
      ...(isNewBot && referrerBotId ? { referrerBotId } : {}),
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw data as ExtractUrlError;
  }

  // Story 6.5: Clear referral after successful bot creation
  if (isNewBot && referrerBotId) {
    clearReferral();
  }

  return data as ExtractUrlResponse;
}

/**
 * Generates a unique ID for tracking extracted URLs
 */
function generateUrlId(): string {
  return `url-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export interface UseUrlExtractionOptions {
  /** Bot ID to associate extractions with */
  botId?: string;
  /** Whether to crawl linked pages */
  crawlLinks?: boolean;
  /** Maximum number of links to follow */
  maxLinks?: number;
  /** Callback when extraction completes successfully */
  onSuccess?: (url: ExtractedUrl, linkedUrls: ExtractedUrl[]) => void;
  /** Callback when extraction fails */
  onError?: (url: ExtractedUrl, error: ExtractUrlError) => void;
}

export interface UseUrlExtractionReturn {
  /** List of extracted URLs with their status */
  urls: ExtractedUrl[];
  /** Whether any extraction is in progress */
  isExtracting: boolean;
  /** Current extraction progress count */
  currentCount: number;
  /** Total extraction count */
  totalCount: number;
  /** Extract content from a URL */
  extractUrl: (url: string) => void;
  /** Remove a URL from the list by local ID */
  removeUrl: (urlId: string) => void;
  /** Remove a URL from the list by database contentId */
  removeByContentId: (contentId: string) => void;
  /** Retry a failed URL extraction */
  retryUrl: (urlId: string) => void;
  /** Clear all URLs */
  clearUrls: () => void;
  /** Total character count across all extracted URLs */
  totalCharacterCount: number;
  /** Bot ID created for URL extractions (shared across all URLs) */
  botId: string | null;
  /** Current extraction summary */
  summary: ExtractionSummary | null;
}

/**
 * Hook for managing URL content extraction with TanStack Query
 *
 * Features:
 * - URL extraction via API
 * - Tracks primary URL and linked pages separately
 * - Progress tracking per URL
 * - Error handling with i18n support
 * - Optimistic updates for better UX
 *
 * @example
 * ```tsx
 * const { urls, isExtracting, extractUrl, removeUrl } = useUrlExtraction({
 *   onSuccess: (url, linked) => console.log('Extracted:', url.title, `+${linked.length} pages`),
 * });
 *
 * return (
 *   <>
 *     <UrlInputField onSubmit={extractUrl} isLoading={isExtracting} />
 *     <UrlPreviewList urls={urls} onRemove={removeUrl} />
 *   </>
 * );
 * ```
 */
export function useUrlExtraction(options?: UseUrlExtractionOptions): UseUrlExtractionReturn {
  const queryClient = useQueryClient();
  const [urls, setUrls] = useState<ExtractedUrl[]>([]);
  const [createdBotId, setCreatedBotId] = useState<string | null>(options?.botId ?? null);
  const [summary, setSummary] = useState<ExtractionSummary | null>(null);

  // URL extraction mutation
  const extractMutation = useMutation({
    mutationFn: ({ url, urlId }: { url: string; urlId: string }) => {
      const botId = createdBotId ?? options?.botId;
      return extractUrl({
        url,
        ...(botId ? { botId } : {}),
        crawlLinks: options?.crawlLinks ?? true,
        maxLinks: options?.maxLinks ?? 10,
      });
    },
    onMutate: async ({ url, urlId }) => {
      // Optimistic update - add URL with extracting status
      const newUrl: ExtractedUrl = {
        id: urlId,
        url,
        status: 'extracting' as UrlStatus,
      };

      setUrls((prev) => [...prev, newUrl]);
    },
    onSuccess: (response, { urlId }) => {
      const { botId: responseBotId, primary, linkedPages, summary: responseSummary } = response.data;

      // Track the bot ID from the API response
      if (responseBotId && !createdBotId) {
        setCreatedBotId(responseBotId);
      }

      // Update summary
      setSummary(responseSummary);

      // Build the primary URL result with contentId for deletion sync
      const primaryUrl: ExtractedUrl = {
        id: urlId,
        contentId: primary.contentId,
        url: primary.url,
        title: primary.title,
        excerpt: primary.excerpt,
        content: primary.excerpt, // Store excerpt as content for preview
        characterCount: primary.characterCount,
        status: primary.isPartial ? ('partial' as UrlStatus) : ('ready' as UrlStatus),
        isPartial: primary.isPartial,
        extractionMethod: primary.extractionMethod,
      };

      // Build linked page results with contentId for deletion sync
      const linkedUrls: ExtractedUrl[] = linkedPages.map((page, index) => ({
        id: `${urlId}-linked-${index}`,
        contentId: page.contentId,
        url: page.url,
        title: page.title,
        excerpt: page.excerpt,
        content: page.excerpt,
        characterCount: page.characterCount,
        status: page.failed ? ('error' as UrlStatus) : ('ready' as UrlStatus),
        ...(page.error ? { errorMessage: page.error } : {}),
        ...(page.failed ? { errorCode: 'extractionFailed' } : {}),
        linkScore: page.linkScore,
        linkText: page.linkText,
      }));

      // Update URLs state
      setUrls((prev) => {
        // Replace the extracting URL with results
        const updated = prev.filter((u) => u.id !== urlId);
        return [...updated, primaryUrl, ...linkedUrls];
      });

      // Invalidate content sources query to refresh the unified list
      if (responseBotId) {
        queryClient.invalidateQueries({ queryKey: ['contentSources', responseBotId] });
      }

      // Call onSuccess callback
      queueMicrotask(() => {
        options?.onSuccess?.(primaryUrl, linkedUrls);
      });
    },
    onError: (error: ExtractUrlError, { urlId }) => {
      const errorKey = mapErrorCodeToI18nKey(error.error.code);

      // Update URL with error status
      setUrls((prev) =>
        prev.map((u) => {
          if (u.id === urlId) {
            return {
              ...u,
              status: 'error' as UrlStatus,
              errorMessage: error.error.message,
              errorCode: errorKey,
            };
          }
          return u;
        })
      );

      // Call onError callback
      const failedUrl = urls.find((u) => u.id === urlId);
      if (failedUrl) {
        queueMicrotask(() => {
          options?.onError?.(failedUrl, error);
        });
      }
    },
  });

  // Extract a URL
  const doExtractUrl = useCallback(
    (url: string) => {
      const urlId = generateUrlId();
      extractMutation.mutate({ url, urlId });
    },
    [extractMutation]
  );

  // Remove a URL from the list and delete from database
  const removeUrl = useCallback(
    async (urlId: string) => {
      // Find the URL to get its contentId
      const urlToRemove = urls.find((u) => u.id === urlId);

      // Remove from local state immediately
      setUrls((prev) => prev.filter((u) => u.id !== urlId));

      // If URL has a contentId, delete from database
      if (urlToRemove?.contentId && createdBotId) {
        try {
          await fetch(`/api/bots/${createdBotId}/content/${urlToRemove.contentId}`, {
            method: 'DELETE',
          });
          // Invalidate content sources to refresh unified panel
          queryClient.invalidateQueries({ queryKey: ['contentSources', createdBotId] });
        } catch (error) {
          console.error('[useUrlExtraction] Failed to delete content from API:', error);
        }
      }
    },
    [urls, createdBotId, queryClient]
  );

  // Clear all URLs and reset state
  const clearUrls = useCallback(() => {
    setUrls([]);
    setCreatedBotId(null);
    setSummary(null);
  }, []);

  // Retry a failed URL extraction
  const retryUrl = useCallback(
    (urlId: string) => {
      const failedUrl = urls.find((u) => u.id === urlId && u.status === 'error');
      if (!failedUrl) return;

      // Remove the failed URL and re-extract with a new ID
      setUrls((prev) => prev.filter((u) => u.id !== urlId));
      const newUrlId = generateUrlId();
      extractMutation.mutate({ url: failedUrl.url, urlId: newUrlId });
    },
    [urls, extractMutation]
  );

  // Remove a URL by its database contentId (for syncing with unified panel)
  const removeByContentId = useCallback((contentId: string) => {
    setUrls((prev) => prev.filter((u) => u.contentId !== contentId));
  }, []);

  // Calculate total character count
  const totalCharacterCount = urls
    .filter((u) => u.status === 'ready' || u.status === 'partial')
    .reduce((acc, u) => acc + (u.characterCount || 0), 0);

  // Calculate progress counts
  const completedUrls = urls.filter((u) => u.status === 'ready' || u.status === 'partial' || u.status === 'error').length;
  const extractingUrls = urls.filter((u) => u.status === 'extracting').length;
  const currentCount = completedUrls;
  const totalCount = completedUrls + extractingUrls;

  return {
    urls,
    isExtracting: extractMutation.isPending,
    currentCount,
    totalCount,
    extractUrl: doExtractUrl,
    removeUrl,
    removeByContentId,
    retryUrl,
    clearUrls,
    totalCharacterCount,
    botId: createdBotId,
    summary,
  };
}

// Export types
export type { ExtractUrlResponse, ExtractUrlError, ExtractionSummary };
